import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, Stack, Chip, Autocomplete, TextField, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';

const TagEditor = ({ sentence, sentenceCallBack, errorCallBack, allContentTags, disabled = false}) => {
  const theme = useTheme();
  const [tags, setTags] = useState([]);
  const [addingTag, setAddingTag] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  useEffect(() => {
    if (sentence) {
      setTags(sentence.tags);
    } else {
      setTags([]);
    }
    setInputValue('');
  }, [sentence]);

  const commitTag = async (value) => {
    const val = typeof value === 'string' ? value.trim() : '';
    if (!val) {
      setAddingTag(false);
      setInputValue('');
      return;
    }

    // Case-insensitive duplicate check
    const valLower = val.toLowerCase();
    if (tags.some((t) => t.toLowerCase() === valLower)) {
      setAddingTag(false);
      setInputValue('');
      return;
    }

    const updated = [...tags, val]; // preserve entered case for display
    setTags(updated);

    if (!sentence) {
      setAddingTag(false);
      setInputValue('');
      return;
    }

    setSaving(true);
    const newSentence = sentence.deepCopy();
    newSentence.tags =updated;
    newSentence.play = false;

    try {
      await newSentence.saveSentence();
      sentenceCallBack(newSentence, newSentence.index);
    } catch (err) {
      if (errorCallBack) errorCallBack(err);
    } finally {
      setSaving(false);
      setAddingTag(false);
      setInputValue('');
    }
  };

  const handleDelete = async (tagToDelete) => {
    const updated = tags.filter(
      (t) => t.toLowerCase() !== tagToDelete.toLowerCase()
    );

    const newSentence = sentence.deepCopy();
    newSentence.tags = updated;
    newSentence.play = false;

    try {
      await newSentence.saveSentence();
      setTags(updated);
      sentenceCallBack(newSentence, newSentence.index);
    } catch (err) {
      if (errorCallBack) errorCallBack(err);
    }
  };


  return (
    <Box sx={{ mb: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton size="small" onClick={() => setAddingTag(true)} disabled={disabled || saving}>
          {saving ? <CircularProgress size={18} /> : <AddIcon fontSize="small" />}
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', ml: 1 }}>
          Tags:
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
        
        {tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            onDelete={() => handleDelete(tag)}
            size="small"
            sx={{ backgroundColor: theme.palette.secondary.light, color: theme.palette.secondary.contrastText }}
          />
        ))}
      </Stack>

      {addingTag && (
        <Autocomplete
          freeSolo
          options={allContentTags || []}
          inputValue={inputValue}
          onInputChange={(event, newInput) => setInputValue(newInput)}
          onChange={(event, newValue) => commitTag(newValue)}
          disabled={disabled || saving}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add tag"
              size="small"
              autoFocus
              onBlur={() => commitTag(inputValue)} // handles iPhone "Done"
            />
          )}
        />
      )}
    </Box>
  );
};

export default TagEditor;
